import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, User, LogIn, Zap } from 'lucide-react';
import { Button } from './ui/button';
import { ThemeToggle } from './ThemeToggle';
import { useAuth } from '../context/AuthContext';
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from './ui/sheet';

// Wolkenhelden Logo URL
const logoUrl = '/images/logo.png';

// Flying Hero SVG Icon
const FlyingHero = ({ className }) => (
  <svg 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round"
    className={className}
  >
    <circle cx="12" cy="5" r="3" />
    <path d="M12 8v4" />
    <path d="M8 14l4-2 4 2" />
    <path d="M6 18l6-4 6 4" />
    <path d="M4 12l2-2" />
    <path d="M20 12l-2-2" />
    <path d="M2 8l3 1" />
    <path d="M22 8l-3 1" />
  </svg>
);

// Cape/Hero Icon
const HeroIcon = ({ className }) => (
  <svg 
    viewBox="0 0 24 24" 
    fill="currentColor" 
    className={className}
  >
    <path d="M12 2C9.243 2 7 4.243 7 7c0 2.757 2.243 5 5 5s5-2.243 5-5c0-2.757-2.243-5-5-5z"/>
    <path d="M12 14c-4.418 0-8 2.239-8 5v1c0 1.105.895 2 2 2h12c1.105 0 2-.895 2-2v-1c0-2.761-3.582-5-8-5z"/>
    <path d="M5 10L2 16l4-1-1 5 3-3 4 2 4-2 3 3-1-5 4 1-3-6" fill="currentColor" opacity="0.3"/>
  </svg>
);

export const Navbar = () => {
  const location = useLocation();
  const { isAuthenticated, user } = useAuth();
  const [isOpen, setIsOpen] = React.useState(false);

  const isActive = (path) => location.pathname === path;

  const NavLink = ({ to, children }) => (
    <Link
      to={to}
      className={`text-sm font-medium transition-colors hover:text-primary ${
        isActive(to) ? 'text-primary' : 'text-muted-foreground'
      }`}
      data-testid={`nav-link-${to.replace('/', '')}`}
    >
      {children}
    </Link>
  );

  const MobileNavLink = ({ to, children }) => (
    <Link
      to={to}
      className={`block py-2 text-base font-medium transition-colors hover:text-primary ${
        isActive(to) ? 'text-primary' : 'text-foreground'
      }`}
      onClick={() => setIsOpen(false)}
      data-testid={`mobile-nav-link-${to.replace('/', '')}`}
    >
      {children}
    </Link>
  );

  return (
    <nav className="sticky top-0 z-50 w-full glass-card border-b" data-testid="navbar">
      <div className="container mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
          <img 
            src={logoUrl} 
            alt="Wolkenhelden Logo" 
            className="h-9 w-9 object-contain"
          />
          <span className="font-display text-xl font-semibold">Wolkenhelden</span>
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden items-center gap-6 md:flex">
          <NavLink to="/">Start</NavLink>
          <NavLink to="/preise">Wolken</NavLink>
          <NavLink to="/domains">Domains</NavLink>
          <NavLink to="/features">Superkräfte</NavLink>
          <NavLink to="/technik">Technik</NavLink>
        </div>

        <div className="hidden items-center gap-3 md:flex">
          {/* Theme Toggle nur auf großen Bildschirmen (>1280px) */}
          <div className="hidden xl:block">
            <ThemeToggle />
          </div>
          
          {isAuthenticated ? (
            <>
              <Button variant="ghost" asChild data-testid="nav-portal-button">
                <Link to="/portal" className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  <span className="max-w-[100px] truncate">{user?.name || user?.email?.split('@')[0]}</span>
                </Link>
              </Button>
              <Button asChild data-testid="nav-cta-button" className="bg-gradient-to-r from-primary to-blue-600 hover:from-primary/90 hover:to-blue-600/90">
                <Link to="/portal" className="flex items-center gap-2">
                  <Zap className="h-4 w-4" />
                  Mein Helden-Portal
                </Link>
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" asChild data-testid="nav-login-button">
                <Link to="/login" className="flex items-center gap-2">
                  <LogIn className="h-4 w-4" />
                  Anmelden
                </Link>
              </Button>
              <Button asChild data-testid="nav-cta-button" className="bg-gradient-to-r from-primary to-blue-600 hover:from-primary/90 hover:to-blue-600/90 shadow-lg shadow-primary/25">
                <Link to="/preise" className="flex items-center gap-2">
                  <Zap className="h-4 w-4 animate-pulse" />
                  Werde Held
                </Link>
              </Button>
            </>
          )}
        </div>

        {/* Mobile Menu */}
        <div className="flex items-center gap-2 md:hidden">
          <ThemeToggle />
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="mobile-menu-button">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent>
              <div className="mt-6 flex flex-col gap-4">
                <MobileNavLink to="/">Start</MobileNavLink>
                <MobileNavLink to="/preise">Wolken</MobileNavLink>
                <MobileNavLink to="/domains">Domains</MobileNavLink>
                <MobileNavLink to="/features">Superkräfte</MobileNavLink>
                <MobileNavLink to="/technik">Technik</MobileNavLink>
                
                <div className="border-t pt-4 mt-2">
                  {isAuthenticated ? (
                    <>
                      <p className="text-sm text-muted-foreground mb-2">
                        Eingeloggt als {user?.email}
                      </p>
                      <Button asChild className="w-full bg-gradient-to-r from-primary to-blue-600">
                        <Link to="/portal" onClick={() => setIsOpen(false)} className="flex items-center justify-center gap-2">
                          <Zap className="h-4 w-4" />
                          Mein Helden-Portal
                        </Link>
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button variant="outline" asChild className="w-full mb-2">
                        <Link to="/login" onClick={() => setIsOpen(false)}>
                          Anmelden
                        </Link>
                      </Button>
                      <Button asChild className="w-full bg-gradient-to-r from-primary to-blue-600">
                        <Link to="/preise" onClick={() => setIsOpen(false)} className="flex items-center justify-center gap-2">
                          <Zap className="h-4 w-4" />
                          Werde Held
                        </Link>
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  );
};
